import { useId, useMemo, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { DISPLAY_NAME_LENGTH, type Me } from '@cribbage/protocol';
import { api } from '~/api/client.js';
import { useAnonymous, useAuthStore, usePlayerId } from '~/store/authStore.js';
import { ownFace } from '~/store/avatars.js';
import { clearLocalGames } from '~/store/localGames.js';
import { type FriendlyError, refusalLine } from '~/store/sessionStore.js';
import { forgetLegacyOptOut, useSettingsStore } from '~/store/settingsStore.js';
import { Avatar } from '~/ui/Avatar.js';
import { Banner } from '~/ui/Banner.js';
import { Button, ButtonLink } from '~/ui/Button.js';
import { countryOptions } from '~/ui/countries.js';
import { FieldLabel } from '~/ui/FieldLabel.js';
import { Section } from '~/ui/Section.js';
import { Select } from '~/ui/Select.js';
import { Switch } from '~/ui/Switch.js';
import { TextField } from '~/ui/TextField.js';
import { useLatestRequest } from '~/ui/useLatestRequest.js';
import { emailProblem, newPasswordHint, newPasswordProblem } from '../accountChecks.js';
import { accountPageLink } from './accountPages.js';
import { longDate } from './subscriptionPages.js';
import { SubmitButton } from './SubmitButton.js';

/**
 * The Account group: who you are, how you sign in, and how to leave.
 *
 * Drawn to the `lobby-account-settings` sketch (2026-09-21). A guest and an
 * account see the same page, so the controls are where they will always be:
 * every install is already a player with a face and a name the game gave it,
 * and a guest's Change chips open the account wall instead of a field. Where
 * the sketch drew something the product cannot do yet, this page leaves it
 * out rather than drawing a promise.
 *
 * A guest's Leave group holds one row, Delete guest data, as the sketch draws
 * it: no Sign out, because a guest has nothing to sign out of (D19).
 *
 * Two rows the sketch dropped stay: Country, which is the player's own choice
 * about their name, and Count my bot games, which `PRIVACY.md` names as the
 * way to stop uploads and clear this device.
 *
 * An account row opens where it stands (Mitch, 2026-09-21): the value becomes
 * the field, the line under it is the hint or the refusal, and Save and
 * Cancel sit at the row's end. Each row owns its own refusal, so a failed
 * email change cannot print itself under the name.
 *
 * The avatar is not a row. The catalog will hold more faces than a row can
 * show, so Change avatar opens its own page (`AvatarPage`), and a guest's chip
 * opens the account wall. Just after joining, the chip is the lemon one,
 * Choose an avatar, because it is the one thing left to do.
 */

export type AccountFlash =
  | 'joined'
  | 'deleted'
  | 'deletedOffline'
  | 'deleteUnconfirmed'
  | 'guestDeleted'
  | 'guestDeleteUnconfirmed'
  | 'passwordReset'
  | 'avatarSaved';

type Row = 'name' | 'email' | 'password';

export function AccountPage({ flash }: { readonly flash: AccountFlash | null }): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const user = useAuthStore((state) => state.user);
  const ready = useAuthStore((state) => state.ready);
  const anonymous = useAnonymous();
  const [editing, setEditing] = useState<Row | null>(null);
  /** The open row as of the last render, for a close that lands after a wait. */
  const open = useRef<Row | null>(null);
  open.current = editing;
  /** The Change chip each row closes back onto, so focus lands where it left. */
  const doors = useRef<Partial<Record<Row, HTMLButtonElement | null>>>({});

  // What the page was sent here to say, whether or not there is a player to
  // show under it: a deletion can leave the device with none until it can
  // reach the server again.
  const banner = (
    <>
      {/* "Open the app again" is only true while there is no player yet: once
          the retry on resume registers a guest, the plain line is the true one. */}
      {(flash === 'deleted' || (flash === 'deletedOffline' && user !== null)) && (
        <Banner role="status">{t('settings.account.deleted')}</Banner>
      )}
      {flash === 'deletedOffline' && user === null && (
        <Banner role="status">{t('settings.account.deletedOffline')}</Banner>
      )}
      {flash === 'deleteUnconfirmed' && (
        <Banner role="status">{t('settings.account.deleteUnconfirmed')}</Banner>
      )}
      {flash === 'guestDeleted' && (
        <Banner role="status">{t('settings.account.guestDeleted')}</Banner>
      )}
      {flash === 'guestDeleteUnconfirmed' && (
        <Banner role="status">{t('settings.account.guestDeleteUnconfirmed')}</Banner>
      )}
      {flash === 'avatarSaved' && user !== null && (
        <Banner role="status" tone="good">
          {t('settings.account.avatarSaved', { name: t(`avatars.${ownFace(user.avatar_id)}`) })}
        </Banner>
      )}
      {/* A reset link opened on a device signed in to an account lands here,
          not on Sign in. See `NewPasswordPage`. */}
      {flash === 'passwordReset' && (
        <Banner role="status" tone="good">
          {t('settings.account.passwordSaved')}
        </Banner>
      )}
    </>
  );

  if (user === null) {
    // No player yet: the first second of a fresh install, or a launch that
    // could not reach the server to register one.
    return (
      <>
        {banner}
        <p className="text-muted" role="status">
          {t(ready ? 'errors.network' : 'common.loading')}
        </p>
      </>
    );
  }

  /*
   * Closes one row, and only if it is still the open one: a save that lands
   * after the player has opened another row must not close that row too.
   */
  const close = (row: Row) => (): void => {
    if (open.current !== row) return;
    open.current = null;
    setEditing(null);
    requestAnimationFrame(() => doors.current[row]?.focus());
  };
  const door = (row: Row) => (element: HTMLButtonElement | null) => {
    doors.current[row] = element;
  };

  const lock = renameLock(user.rename_available_at);
  // The face is named, not only shown, so the preview says which one it is.
  const avatarLabel = t('settings.account.avatar', {
    name: t(`avatars.${ownFace(user.avatar_id)}`),
  });
  /*
   * A chosen name waits for moderation, and until it clears `display_name`
   * is still the public one, which for a new account is the name the game
   * gave it. So the page greets nobody by name, and the Name row shows the
   * name the player chose (`pending_display_name`), marked as being checked,
   * with the name everybody else sees meanwhile. Showing the old name alone
   * read as a rename that had failed. Change waits too: the server refuses a
   * second name while one is pending.
   */
  const pending = !anonymous && user.display_name_moderation_status === 'pending';

  return (
    <>
      {flash === 'joined' && !anonymous && (
        <Banner role="status" tone="good">
          {pending
            ? t('settings.account.joinedPending')
            : t('settings.account.joined', { name: user.display_name })}
        </Banner>
      )}
      {banner}

      <Section title={t('settings.account.whoYouAre')}>
        <div className="divide-y divide-gray-300">
          <div className="flex items-center justify-between gap-3 pb-3">
            <Avatar kind="you" side="rows" size="hero" name={avatarLabel} />
            {anonymous ? (
              <ButtonLink size="sm" {...accountPageLink('need-avatar')}>
                {t('settings.account.changeAvatar')}
              </ButtonLink>
            ) : (
              <ButtonLink
                size="sm"
                variant={flash === 'joined' ? 'primary' : 'default'}
                {...accountPageLink('avatar')}
              >
                {t(
                  flash === 'joined'
                    ? 'settings.account.chooseAvatar'
                    : 'settings.account.changeAvatar',
                )}
              </ButtonLink>
            )}
          </div>
          {editing === 'name' ? (
            <NameEdit current={user.display_name} onClose={close('name')} />
          ) : (
            <AccountRow
              label={t('settings.account.name')}
              hint={user.pending_display_name ?? user.display_name}
              note={
                anonymous
                  ? t('settings.account.guestName')
                  : pending
                    ? t('settings.account.namePending', { name: user.display_name })
                    : lock === null
                      ? undefined
                      : t('settings.renameLocked', {
                          date: longDate(lock.toISOString(), i18n.language),
                        })
              }
            >
              {anonymous ? (
                <ButtonLink
                  size="sm"
                  {...accountPageLink('need-name')}
                  aria-label={t('settings.account.changeName')}
                >
                  {t('settings.account.change')}
                </ButtonLink>
              ) : (
                <Button
                  ref={door('name')}
                  size="sm"
                  disabled={lock !== null || pending}
                  aria-label={t('settings.account.changeName')}
                  onClick={() => {
                    setEditing('name');
                  }}
                >
                  {t('settings.account.change')}
                </Button>
              )}
            </AccountRow>
          )}
          <CountryRow country={user.country} />
        </div>
      </Section>

      {anonymous ? (
        <Section title={t('settings.account.yourAccount')}>
          <p className="text-muted">{t('settings.account.guestWhy')}</p>
          <div className="mt-4 flex flex-wrap justify-end gap-3">
            <ButtonLink variant="primary" {...accountPageLink('register')}>
              {t('settings.account.createAccount')}
            </ButtonLink>
            <ButtonLink {...accountPageLink('sign-in')}>{t('auth.signIn')}</ButtonLink>
          </div>
        </Section>
      ) : (
        user.email !== null && (
          // An account with only Apple or Google behind it has no email or
          // password of its own to change here.
          <Section title={t('settings.account.signInDetails')}>
            <div className="divide-y divide-gray-300">
              {editing === 'email' ? (
                <EmailEdit current={user.email} onClose={close('email')} />
              ) : (
                <AccountRow label={t('auth.email')} hint={user.email}>
                  <Button
                    ref={door('email')}
                    size="sm"
                    aria-label={t('settings.account.changeEmail')}
                    onClick={() => {
                      setEditing('email');
                    }}
                  >
                    {t('settings.account.change')}
                  </Button>
                </AccountRow>
              )}
              {editing === 'password' ? (
                <PasswordEdit onClose={close('password')} />
              ) : (
                <AccountRow label={t('auth.password')} hint={t('settings.account.passwordHint')}>
                  <Button
                    ref={door('password')}
                    size="sm"
                    aria-label={t('settings.account.changePassword')}
                    onClick={() => {
                      setEditing('password');
                    }}
                  >
                    {t('settings.account.change')}
                  </Button>
                </AccountRow>
              )}
            </div>
          </Section>
        )
      )}

      <Section title={t('settings.account.stats')}>
        <SyncSwitch />
      </Section>

      <LeaveSection guest={anonymous} />
    </>
  );
}

/**
 * One account row: what it is, one line about it, and the control at the end.
 *
 * `FieldLabel` is the name-and-hint half every settings row shares. The rule
 * between rows is the list's `divide-y`, because these rows are not `<label>`s
 * for `Section` to rule off: the control is a button, not an input.
 */
function AccountRow({
  label,
  hint,
  note,
  children,
}: {
  readonly label: string;
  readonly hint: string;
  /** A smaller line under the hint, for where the value came from. */
  readonly note?: string | undefined;
  readonly children?: ReactNode;
}): React.JSX.Element {
  return (
    <div className="flex items-center gap-3 py-3 last:pb-0">
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <FieldLabel hint={hint}>{label}</FieldLabel>
        {note !== undefined && <span className="text-sm text-muted italic">{note}</span>}
      </div>
      {children === undefined ? null : <div className="flex-none">{children}</div>}
    </div>
  );
}

/** Save and Cancel at the end of an open row, at chip size. */
function EditFoot({
  busy,
  onCancel,
}: {
  readonly busy: boolean;
  readonly onCancel: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="flex justify-end gap-3">
      <Button size="sm" variant="ghost" onClick={onCancel}>
        {t('common.cancel')}
      </Button>
      <SubmitButton size="sm" busy={busy}>
        {t('settings.save')}
      </SubmitButton>
    </div>
  );
}

/**
 * The line under one field for a refusal from the server.
 *
 * Rails names the field it refused, and its message is written to follow the
 * field's name ("is incorrect"). A refusal that names none of this form's
 * fields, a dropped connection for one, lands on the field marked `fallback`,
 * the last one before Save, so it is never lost.
 */
function refusalFor(
  error: FriendlyError | null,
  field: string,
  fields: readonly string[],
  fallback: boolean,
  t: TFunction,
): string | undefined {
  if (error === null) return undefined;
  const messages = error.details?.[field];
  if (messages !== undefined && messages.length > 0) {
    return refusalLine(field, messages);
  }
  if (!fallback) return undefined;
  const named = fields.some((name) => (error.details?.[name]?.length ?? 0) > 0);
  return named ? undefined : t(error.key);
}

/**
 * A row's own save: its busy flag, its refusal, and the store call that adopts
 * the answer. Save turns off while the request is out, which drops focus to
 * the page, so a refusal hands focus to the field it names: the reader lands
 * on the line that says what went wrong, with what they typed still there.
 */
function useRowSave(onSaved: () => void): {
  readonly form: React.RefObject<HTMLFormElement | null>;
  readonly busy: boolean;
  readonly error: FriendlyError | null;
  readonly save: (call: () => Promise<Me>) => Promise<void>;
} {
  const form = useRef<HTMLFormElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<FriendlyError | null>(null);
  // The row unmounts when it closes or another opens, which retires its save:
  // the store still adopts the answer, and this row no longer acts on it.
  const request = useLatestRequest();
  const save = async (call: () => Promise<Me>): Promise<void> => {
    const isCurrent = request.begin();
    setBusy(true);
    setError(null);
    const failure = await useAuthStore.getState().saveMe(call);
    if (!isCurrent()) return;
    setBusy(false);
    if (failure === null) {
      onSaved();
      return;
    }
    setError(failure);
    focusRefusal(form);
  };
  return { form, busy, error, save };
}

/** Focus the first field marked refused, once the refusal has rendered. */
function focusRefusal(form: React.RefObject<HTMLFormElement | null>): void {
  requestAnimationFrame(() => {
    form.current?.querySelector<HTMLInputElement>('[aria-invalid="true"]')?.focus();
  });
}

function NameEdit({
  current,
  onClose,
}: {
  readonly current: string;
  readonly onClose: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const [name, setName] = useState(current);
  const [tooShort, setTooShort] = useState(false);
  const { form, busy, error, save } = useRowSave(onClose);
  return (
    <form
      ref={form}
      className="flex flex-col gap-3 py-3"
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        const trimmed = name.trim();
        if (trimmed === current) {
          onClose();
          return;
        }
        if (trimmed.length < DISPLAY_NAME_LENGTH.min) {
          setTooShort(true);
          focusRefusal(form);
          return;
        }
        setTooShort(false);
        void save(() => api.updateMe({ display_name: trimmed }));
      }}
    >
      <TextField
        label={t('settings.account.name')}
        hint={t('auth.displayNameHint')}
        type="text"
        autoComplete="nickname"
        maxLength={DISPLAY_NAME_LENGTH.max}
        autoFocus
        value={name}
        error={
          tooShort
            ? t('auth.displayNameLength', {
                min: DISPLAY_NAME_LENGTH.min,
                max: DISPLAY_NAME_LENGTH.max,
              })
            : refusalFor(error, 'display_name', ['display_name'], true, t)
        }
        onChange={(event) => {
          setName(event.target.value);
        }}
      />
      <EditFoot busy={busy} onCancel={onClose} />
    </form>
  );
}

/**
 * Email asks for the password too, because a new sign-in address is the one
 * change that can lock a player out. Every refusal keeps what was typed.
 */
function EmailEdit({
  current,
  onClose,
}: {
  readonly current: string;
  readonly onClose: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [problems, setProblems] = useState<{ email?: string; password?: string }>({});
  const { form, busy, error, save } = useRowSave(onClose);
  const fields = ['email', 'current_password'];
  return (
    <form
      ref={form}
      className="flex flex-col gap-3 py-3"
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        const found: { email?: string; password?: string } = {};
        const invalid = emailProblem(email, t);
        if (invalid !== undefined) found.email = invalid;
        if (password === '') found.password = t('auth.passwordRequired');
        setProblems(found);
        if (Object.keys(found).length > 0) {
          focusRefusal(form);
          return;
        }
        void save(() => api.changeEmail(email.trim(), password));
      }}
    >
      <TextField
        label={t('settings.account.newEmail')}
        hint={t('settings.account.nowEmail', { email: current })}
        type="email"
        autoComplete="email"
        autoFocus
        value={email}
        error={problems.email ?? refusalFor(error, 'email', fields, false, t)}
        onChange={(event) => {
          setEmail(event.target.value);
        }}
      />
      <TextField
        label={t('auth.password')}
        hint={t('settings.account.confirmHint')}
        type="password"
        autoComplete="current-password"
        value={password}
        error={problems.password ?? refusalFor(error, 'current_password', fields, true, t)}
        onChange={(event) => {
          setPassword(event.target.value);
        }}
      />
      <EditFoot busy={busy} onCancel={onClose} />
    </form>
  );
}

function PasswordEdit({ onClose }: { readonly onClose: () => void }): React.JSX.Element {
  const { t } = useTranslation();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [problems, setProblems] = useState<{ current?: string; next?: string }>({});
  const { form, busy, error, save } = useRowSave(onClose);
  const fields = ['current_password', 'password'];
  return (
    <form
      ref={form}
      className="flex flex-col gap-3 py-3"
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        const found: { current?: string; next?: string } = {};
        if (current === '') found.current = t('auth.passwordRequired');
        const short = newPasswordProblem(next, t);
        if (short !== undefined) found.next = short;
        setProblems(found);
        if (Object.keys(found).length > 0) {
          focusRefusal(form);
          return;
        }
        void save(() => api.changePassword(current, next));
      }}
    >
      <TextField
        label={t('settings.account.currentPassword')}
        type="password"
        autoComplete="current-password"
        autoFocus
        value={current}
        error={problems.current ?? refusalFor(error, 'current_password', fields, false, t)}
        onChange={(event) => {
          setCurrent(event.target.value);
        }}
      />
      <TextField
        label={t('settings.account.newPassword')}
        hint={newPasswordHint(t)}
        type="password"
        autoComplete="new-password"
        value={next}
        error={problems.next ?? refusalFor(error, 'password', fields, true, t)}
        onChange={(event) => {
          setNext(event.target.value);
        }}
      />
      <EditFoot busy={busy} onCancel={onClose} />
    </form>
  );
}

/**
 * Where the flag beside the name comes from. A guest may choose one: it is
 * about the name they play under, not about having an account.
 */
function CountryRow({ country }: { readonly country: string | null }): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const ids = useId();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<FriendlyError | null>(null);
  const request = useLatestRequest();
  // 250 entries, named and collated for the reader's language: worth a memo.
  const countries = useMemo(() => countryOptions(i18n.language), [i18n.language]);

  const choose = async (code: string): Promise<void> => {
    const isCurrent = request.begin();
    setBusy(true);
    setError(null);
    const failure = await useAuthStore
      .getState()
      .saveMe(() => api.updateMe({ country: code === '' ? null : code }));
    if (!isCurrent()) return;
    setBusy(false);
    setError(failure);
  };

  return (
    <div className="flex flex-col gap-2 py-3 last:pb-0">
      {/* A `<span>` and not a `<label>`: `Select` is a combobox button, and a
          label wrapped round one names it and then swallows its value. The
          name reaches it as `aria-labelledby`. */}
      <FieldLabel hint={t('settings.countryHint')}>
        <span id={`${ids}-country`}>{t('settings.country')}</span>
      </FieldLabel>
      <Select
        value={country ?? ''}
        disabled={busy}
        aria-labelledby={`${ids}-country`}
        options={[
          { value: '', label: t('settings.countryNone') },
          ...countries.map((entry) => ({
            value: entry.code,
            label: entry.flag === '' ? entry.name : `${entry.flag}  ${entry.name}`,
          })),
        ]}
        onChange={(next) => {
          void choose(next);
        }}
      />
      {error !== null && <Banner role="alert">{t(error.key)}</Banner>}
    </div>
  );
}

/**
 * Count my bot games. `PRIVACY.md` sends a player here to stop their games
 * leaving the device, so it stays in Account whatever the sketch drew.
 *
 * Turning it off also clears what is already on the device: a player who says
 * no is not saying "stop from now on". Their own games only, because a device
 * holds games for everyone who has played on it and this answer is one
 * player's.
 */
function SyncSwitch(): React.JSX.Element {
  const { t } = useTranslation();
  const sync = useSettingsStore((state) => state.syncOfflineGames);
  const playerId = usePlayerId();

  const setSync = (value: boolean): void => {
    const settings = useSettingsStore.getState();
    settings.set('syncOfflineGames', value);
    // Somebody has answered the question the pre-v7 marker was standing in for.
    forgetLegacyOptOut();
    // The answer is recorded against whoever gave it, so the next person to
    // sign in on this install cannot turn it back on over their head.
    if (playerId === null) return;
    const others = settings.syncOptOutPlayerIds.filter((id) => id !== playerId);
    settings.set('syncOptOutPlayerIds', value ? others : [...others, playerId]);
  };

  return (
    <Switch
      label={t('settings.syncOffline')}
      hint={t('settings.syncOfflineHint')}
      checked={sync}
      onChange={(value) => {
        setSync(value);
        if (!value) void clearLocalGames(playerId);
      }}
    />
  );
}

/**
 * Sign out and delete, side by side with a line each (`settings.md`, D19).
 * Delete is an ordinary chip: the care is in the page it opens.
 */
function LeaveSection({ guest }: { readonly guest: boolean }): React.JSX.Element {
  const { t } = useTranslation();
  const signingOut = useAuthStore((state) => state.signingOut);
  const [error, setError] = useState<FriendlyError | null>(null);

  /*
   * A sign-out that could not reach the server leaves the account session live,
   * so the store keeps the player signed in and says why. The page stays, and
   * turns into the guest's page when the swap lands.
   */
  const signOut = async (): Promise<void> => {
    setError(null);
    if (!(await useAuthStore.getState().logout())) setError(useAuthStore.getState().error);
  };

  return (
    <Section title={t('settings.account.leave')}>
      <div className="divide-y divide-gray-300">
        {!guest && (
          <AccountRow label={t('profile.signOut')} hint={t('settings.account.signOutHint')}>
            <Button
              size="sm"
              disabled={signingOut}
              onClick={() => {
                void signOut();
              }}
            >
              {t('profile.signOut')}
            </Button>
          </AccountRow>
        )}
        <AccountRow
          label={t(guest ? 'settings.account.deleteGuest' : 'profile.deleteAccount')}
          hint={t(guest ? 'settings.account.deleteGuestHint' : 'settings.account.deleteHint')}
        >
          <ButtonLink size="sm" {...accountPageLink('delete')}>
            {t('settings.account.delete')}
          </ButtonLink>
        </AccountRow>
      </div>
      {error !== null && (
        <Banner role="alert" className="mt-3">
          {t(error.key)}
        </Banner>
      )}
    </Section>
  );
}

/** The lock, or null if a rename is allowed now. */
function renameLock(at: string | null): Date | null {
  if (at === null) return null;
  const when = new Date(at);
  if (Number.isNaN(when.getTime()) || when.getTime() <= Date.now()) return null;
  return when;
}
